import React, { Component } from 'react';
import {
  Box,
  Paper,
  Stack,
  Typography,
  LinearProgress,
  Button,
  Chip,
  CircularProgress,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  ToggleButton,
  ToggleButtonGroup
} from '@mui/material';
import TranslateIcon from '@mui/icons-material/Translate';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import { getAggregate, getFreeText } from '../services/deactivationFeedbackService';
import PeriodSelector from './PeriodSelector';
import { rowsToCSV, downloadCSV } from '../lib/csv';

// Libellés FR affichés. Les CLÉS machine sont la taxonomie du backend (contrat C1,
// `api/src/lib/reasonCodes.js`) : 11 motifs canoniques + 3 codes legacy encore émis par le
// parc en 5.0.x. Propriétaire UNIQUE de ces libellés (contrat C23 / D132) — l'API ne renvoie
// que des codes, aucun libellé (D11). Le repli `|| r.reason_code` plus bas reste : un code
// inconnu s'affiche en clair plutôt que de disparaître.
const REASON_LABELS = {
  // 11 canoniques — ordre d'affichage du plugin (D131) ; ici l'ordre vient du tri par volume.
  back_to_native: 'Retour au natif WordPress',
  switching_competitor: 'Passage à un concurrent',
  didnt_work: 'Ça ne marchait pas',
  too_complex: 'Trop compliqué / trop lourd',
  missing_or_price: 'Fonction manquante ou prix',
  missing_language: 'Langue manquante',
  comments_disabled: 'Commentaires arrêtés',
  site_inactive: 'Site fermé, en pause ou migré',
  just_testing: 'Simple essai',
  temporary: 'Désactivation temporaire',
  other: 'Autre',
  // 3 legacy 5.0.x — encore majoritaires tant que le parc n'a pas migré.
  bug: 'Bug',
  performance: 'Performance',
  no_longer_needed: 'Plus besoin'
};

// Les 8 familles d'analyse de `by_family` (contrat C1). Chaque motif appartient à
// exactement une famille, donc la somme des familles = le total affiché.
const FAMILY_LABELS = {
  back_to_native: 'Retour au natif WordPress',
  competitive: 'Passage à un concurrent',
  reliability: 'Fiabilité et performance',
  onboarding: 'Prise en main',
  product_fit: 'Fonctionnalités et prix',
  comments_strategy: 'Arrêt des commentaires',
  not_product_churn: 'Départ non lié au produit',
  unknown: 'Indéterminé'
};

const VERBATIMS_PAGE_SIZE = 50; // = FREE_TEXT_PAGE_SIZE côté API (pagination 1-based)
const DAY_MS = 24 * 60 * 60 * 1000;
const DEFAULT_WINDOW_DAYS = 90; // aligné sur le dashboard Funnel (B3_01)

// YYYY-MM-DD : valeur d'un <input type="date"> ET borne envoyée à l'endpoint.
function toInputDate(date) {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

// share = fraction 0..1 renvoyée par l'API → pourcentage affiché.
const pct = (share) => `${((Number(share) || 0) * 100).toFixed(1)} %`;

class DeactivationFeedback extends Component {
  constructor(props) {
    super(props);
    const to = new Date();
    const from = new Date(to.getTime() - DEFAULT_WINDOW_DAYS * DAY_MS);
    this.state = {
      from: toInputDate(from),
      to: toInputDate(to),
      aggregate: null,
      isLoading: false,
      error: null,
      verbatims: null,
      verbatimsPage: 1,
      isLoadingVerbatims: false,
      verbatimsError: null,
      // 'detail'   → `by_reason` : un motif = une ligne, codes bruts (comportement historique)
      // 'grouped'  → `by_reason_canonical` : les anciens motifs recomptés sous leur équivalent
      //   neuf (« bug » et « performance » sous « ça ne marchait pas »). Utile tant que les
      //   deux versions du plugin cohabitent dans le parc : sans ça, une même cause remonte
      //   en trois lignes dont aucune ne pèse lourd.
      reasonView: 'detail'
    };
    this.onRefresh = this.onRefresh.bind(this);
    this.goToPage = this.goToPage.bind(this);
    this.handlePeriodChange = this.handlePeriodChange.bind(this);
    this.applyPeriod = this.applyPeriod.bind(this);
    this.exportCSV = this.exportCSV.bind(this);
    this.onReasonViewChange = this.onReasonViewChange.bind(this);
  }

  // ToggleButtonGroup rend `null` quand on re-clique le bouton déjà actif : on ignore, il
  // faut toujours une vue sélectionnée.
  onReasonViewChange(e, value) {
    if (value) this.setState({ reasonView: value });
  }

  componentDidMount() {
    this._isMounted = true;
    this.fetchAll();
  }

  componentWillUnmount() {
    this._isMounted = false;
  }

  // Recharge l'agrégat + la 1ʳᵉ page des verbatims pour la période courante.
  fetchAll() {
    const { from, to } = this.state;
    this.setState({ isLoading: true, error: null });
    getAggregate(from, to)
      .then((result) => {
        if (!this._isMounted) return;
        this.setState({ aggregate: result, isLoading: false });
      })
      .catch((err) => {
        if (!this._isMounted) return;
        this.setState({ error: (err && err.message) || 'Erreur de chargement', isLoading: false });
      });
    this.fetchVerbatims(1);
  }

  // Lecture seule : le front affiche, n'agrège pas. Verbatims sensibles, paginés (50/page, 1-based).
  fetchVerbatims(page) {
    const { from, to } = this.state;
    this.setState({ isLoadingVerbatims: true, verbatimsError: null, verbatimsPage: page });
    getFreeText(from, to, page)
      .then((result) => {
        if (!this._isMounted) return;
        this.setState({ verbatims: result, isLoadingVerbatims: false });
      })
      .catch((err) => {
        if (!this._isMounted) return;
        this.setState({
          verbatimsError: (err && err.message) || 'Erreur de chargement',
          isLoadingVerbatims: false
        });
      });
  }

  onRefresh(e) {
    if (e && e.preventDefault) e.preventDefault();
    this.fetchAll();
  }

  // Édition manuelle d'un champ date : on stocke, le rechargement attend le « Rafraîchir ».
  handlePeriodChange(from, to) {
    this.setState({ from, to });
  }

  // Raccourci de période (7j/30j/trimestre) : on stocke ET on recharge immédiatement.
  applyPeriod(from, to) {
    this.setState({ from, to }, this.fetchAll);
  }

  // Export CSV côté navigateur des 4 répartitions affichées (motifs, grandes catégories,
  // versions, langues). Les motifs sont toujours exportés en DÉTAIL brut, quel que soit le
  // sélecteur de vue à l'écran : le CSV doit rester la donnée source, pas une lecture.
  // en sections séparées par une ligne vide. Les verbatims free_text (sensibles, PII) sont
  // EXCLUS par décision (D4 / L6). Parts en % à une décimale, séparateur décimal FR.
  exportCSV() {
    const { aggregate, from, to } = this.state;
    if (!aggregate) return;
    const byReason = Array.isArray(aggregate.by_reason) ? aggregate.by_reason : [];
    const byFamily = Array.isArray(aggregate.by_family) ? aggregate.by_family : [];
    const byVersion = Array.isArray(aggregate.by_version) ? aggregate.by_version : [];
    const byLocale = Array.isArray(aggregate.by_locale) ? aggregate.by_locale : [];
    const fmtPct = (share) => ((Number(share) || 0) * 100).toFixed(1).replace('.', ',');
    const rows = [];
    rows.push(['Motif', 'Nombre', 'Part (%)']);
    byReason.forEach((r) => rows.push([
      REASON_LABELS[r.reason_code] || r.reason_code || '(inconnu)',
      String(Number(r.count) || 0),
      fmtPct(r.share)
    ]));
    // Section omise si l'API ne renvoie pas encore `by_family` : un en-tête suivi de rien
    // ferait croire à une catégorie vide plutôt qu'à une donnée absente.
    if (byFamily.length) {
      rows.push([]);
      rows.push(['Grande catégorie', 'Nombre', 'Part (%)']);
      byFamily.forEach((r) => rows.push([
        FAMILY_LABELS[r.family] || r.family || '(inconnue)',
        String(Number(r.count) || 0),
        fmtPct(r.share)
      ]));
    }
    rows.push([]);
    rows.push(['Version', 'Nombre', 'Part (%)']);
    byVersion.forEach((r) => rows.push([
      r.plugin_version != null && r.plugin_version !== '' ? r.plugin_version : '(non précisée)',
      String(Number(r.count) || 0),
      fmtPct(r.share)
    ]));
    rows.push([]);
    rows.push(['Langue', 'Nombre', 'Part (%)']);
    byLocale.forEach((r) => rows.push([
      r.locale != null && r.locale !== '' ? r.locale : '(non précisée)',
      String(Number(r.count) || 0),
      fmtPct(r.share)
    ]));
    downloadCSV(`feedbacks_${from}_${to}.csv`, rowsToCSV(rows));
  }

  goToPage(page) {
    const { verbatims, isLoadingVerbatims } = this.state;
    const total = verbatims ? Number(verbatims.total) || 0 : 0;
    const totalPages = Math.max(1, Math.ceil(total / VERBATIMS_PAGE_SIZE));
    if (page < 1 || page > totalPages || isLoadingVerbatims) return;
    this.fetchVerbatims(page);
  }

  formatDate(value) {
    if (!value) return '';
    const d = new Date(value);
    return Number.isNaN(d.getTime()) ? '' : d.toLocaleDateString('fr-FR');
  }

  // Barres de répartition (déjà triées count desc par l'API). Sert aux motifs ET aux
  // grandes catégories : `keyField` désigne la clé machine de la ligne, `labels` la table
  // de libellés correspondante.
  // Le motif « langue manquante » est mis en avant : croisé au tableau « Par langue »,
  // c'est le backlog de traduction priorisé (l'agrégat ne fournit pas le cross-tab direct).
  renderReasonBars(rows, total, keyField, labels) {
    if (!rows.length || total === 0) {
      return (
        <Typography variant="body2" color="text.secondary" sx={{ py: 3, textAlign: 'center' }}>
          Aucun feedback sur la période sélectionnée.
        </Typography>
      );
    }
    return (
      <Stack spacing={1.5}>
        {rows.map((r) => {
          const key = r[keyField];
          const isLang = keyField === 'reason_code' && key === 'missing_language';
          const share = Number(r.share) || 0;
          return (
            <Box key={key || 'unknown'}>
              <Stack direction="row" justifyContent="space-between" alignItems="baseline" sx={{ mb: 0.25 }}>
                <Stack direction="row" spacing={1} alignItems="center">
                  <Typography variant="subtitle2">
                    {labels[key] || key || '(inconnu)'}
                  </Typography>
                  {isLang && (
                    <Chip
                      size="small"
                      color="info"
                      variant="outlined"
                      icon={<TranslateIcon />}
                      label="signal traduction"
                    />
                  )}
                </Stack>
                <Typography variant="body2" color="text.secondary">
                  {r.count}{' '}
                  <Typography component="span" variant="caption">({pct(share)})</Typography>
                </Typography>
              </Stack>
              <LinearProgress
                variant="determinate"
                value={Math.max(0, Math.min(100, share * 100))}
                color={isLang ? 'info' : 'primary'}
                sx={{ height: 12, borderRadius: 1 }}
              />
            </Box>
          );
        })}
      </Stack>
    );
  }

  // Tableau ventilé générique (version / langue). `emptyValueLabel` = libellé d'une valeur nulle
  // (plugin_version / locale peuvent être null quand le champ est absent côté survey).
  renderBreakdownTable(rows, keyField, headLabel, emptyValueLabel) {
    if (!rows.length) {
      return (
        <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
          Aucune donnée.
        </Typography>
      );
    }
    return (
      <TableContainer>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>{headLabel}</TableCell>
              <TableCell align="right">Nombre</TableCell>
              <TableCell align="right">Part</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((r, i) => {
              const value = r[keyField];
              const display = value != null && value !== '' ? value : emptyValueLabel;
              return (
                <TableRow key={`${value == null ? 'null' : value}-${i}`}>
                  <TableCell>{display}</TableCell>
                  <TableCell align="right">{r.count}</TableCell>
                  <TableCell align="right">{pct(r.share)}</TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
    );
  }

  renderVerbatims() {
    const { verbatims, isLoadingVerbatims, verbatimsError, verbatimsPage } = this.state;
    const items = verbatims && Array.isArray(verbatims.items) ? verbatims.items : [];
    const total = verbatims ? Number(verbatims.total) || 0 : 0;
    const totalPages = Math.max(1, Math.ceil(total / VERBATIMS_PAGE_SIZE));

    return (
      <Paper variant="outlined" sx={{ p: 2 }}>
        <Typography variant="subtitle1" sx={{ mb: 1 }}>
          Commentaires libres{total > 0 ? ` (${total})` : ''}
        </Typography>
        <Alert severity="warning" sx={{ mb: 2 }}>
          Donnée sensible — ces commentaires sont saisis librement par les utilisateurs et peuvent
          contenir des informations personnelles. Réservé aux administrateurs ; ne pas exporter ni diffuser.
        </Alert>

        {isLoadingVerbatims && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
            <CircularProgress size={28} />
          </Box>
        )}

        {!isLoadingVerbatims && verbatimsError && (
          <Alert severity="error">Impossible de charger les commentaires : {verbatimsError}</Alert>
        )}

        {!isLoadingVerbatims && !verbatimsError && items.length === 0 && (
          <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
            Aucun commentaire libre sur la période sélectionnée.
          </Typography>
        )}

        {!isLoadingVerbatims && !verbatimsError && items.length > 0 && (
          <Stack spacing={1.5}>
            {items.map((it, i) => (
              <Box key={i} sx={{ borderLeft: 3, borderColor: 'divider', pl: 1.5 }}>
                <Stack
                  direction="row"
                  spacing={1}
                  alignItems="center"
                  sx={{ mb: 0.5, flexWrap: 'wrap', rowGap: 0.5 }}
                >
                  <Chip size="small" label={REASON_LABELS[it.reason_code] || it.reason_code || '(inconnu)'} />
                  {/* Sous-motifs cochés au 2ᵉ niveau du formulaire : sans eux, on ne sait pas
                      à quoi répond un verbatim du type « il manquait le tri par date ».
                      Affichés en code machine — leurs libellés FR vivent dans le plugin. */}
                  {Array.isArray(it.sub_reasons) &&
                    it.sub_reasons.map((sub) => (
                      <Chip key={sub} size="small" variant="outlined" label={sub} />
                    ))}
                  <Typography variant="caption" color="text.secondary">
                    {this.formatDate(it.occurred_at)}
                  </Typography>
                </Stack>
                <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>{it.free_text}</Typography>
              </Box>
            ))}
          </Stack>
        )}

        {total > VERBATIMS_PAGE_SIZE && (
          <Stack direction="row" spacing={2} alignItems="center" justifyContent="center" sx={{ mt: 2 }}>
            <Button
              size="small"
              variant="outlined"
              disabled={verbatimsPage <= 1 || isLoadingVerbatims}
              onClick={() => this.goToPage(verbatimsPage - 1)}
            >
              Précédent
            </Button>
            <Typography variant="body2" color="text.secondary">
              Page {verbatimsPage} / {totalPages}
            </Typography>
            <Button
              size="small"
              variant="outlined"
              disabled={verbatimsPage >= totalPages || isLoadingVerbatims}
              onClick={() => this.goToPage(verbatimsPage + 1)}
            >
              Suivant
            </Button>
          </Stack>
        )}
      </Paper>
    );
  }

  render() {
    const { from, to, aggregate, isLoading, error, reasonView } = this.state;
    const byReason = aggregate && Array.isArray(aggregate.by_reason) ? aggregate.by_reason : [];
    const byVersion = aggregate && Array.isArray(aggregate.by_version) ? aggregate.by_version : [];
    const byLocale = aggregate && Array.isArray(aggregate.by_locale) ? aggregate.by_locale : [];
    const total = aggregate ? Number(aggregate.total) || 0 : 0;
    // `by_family` et `by_reason_canonical` n'existent qu'à partir de la version d'API qui les
    // livre. L'écran et l'API sont déployés dans la même vague mais pas au même instant : tant
    // que la clé est absente, on masque simplement le bloc et le sélecteur — jamais d'erreur,
    // jamais de tableau vide trompeur.
    const byFamily = aggregate && Array.isArray(aggregate.by_family) ? aggregate.by_family : [];
    const byReasonCanonical =
      aggregate && Array.isArray(aggregate.by_reason_canonical) ? aggregate.by_reason_canonical : [];
    const peutRegrouper = byReasonCanonical.length > 0;
    const motifsAffiches = reasonView === 'grouped' && peutRegrouper ? byReasonCanonical : byReason;

    return (
      <Box sx={{ p: 2 }}>
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          spacing={2}
          alignItems={{ xs: 'stretch', md: 'center' }}
          justifyContent="space-between"
          sx={{ mb: 2 }}
        >
          <Typography variant="h6">Feedbacks de désinstallation</Typography>
          <PeriodSelector
            from={from}
            to={to}
            isLoading={isLoading}
            onChange={this.handlePeriodChange}
            onApplyPeriod={this.applyPeriod}
            onRefresh={this.onRefresh}
            actions={
              <Button
                variant="outlined"
                size="small"
                startIcon={<FileDownloadOutlinedIcon />}
                disabled={isLoading || !aggregate || total === 0}
                onClick={this.exportCSV}
              >
                Exporter CSV
              </Button>
            }
          />
        </Stack>

        {isLoading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
            <CircularProgress />
          </Box>
        )}

        {!isLoading && error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            Impossible de charger les feedbacks : {error}
          </Alert>
        )}

        {!isLoading && !error && aggregate && (
          <Stack spacing={3}>
            <Paper variant="outlined" sx={{ p: 2 }}>
              <Stack
                direction={{ xs: 'column', sm: 'row' }}
                spacing={1}
                alignItems={{ xs: 'flex-start', sm: 'center' }}
                justifyContent="space-between"
                sx={{ mb: 2 }}
              >
                <Typography variant="subtitle1">
                  Répartition des motifs{' '}
                  <Typography component="span" variant="caption" color="text.secondary">
                    ({total} feedback{total > 1 ? 's' : ''} · {from} → {to})
                  </Typography>
                </Typography>
                {peutRegrouper && (
                  <ToggleButtonGroup
                    size="small"
                    exclusive
                    value={reasonView}
                    onChange={this.onReasonViewChange}
                  >
                    <ToggleButton value="detail">Détail</ToggleButton>
                    <ToggleButton value="grouped">Regroupé</ToggleButton>
                  </ToggleButtonGroup>
                )}
              </Stack>
              {reasonView === 'grouped' && peutRegrouper && (
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5 }}>
                  Les anciens motifs du plugin sont recomptés sous leur équivalent actuel
                  («&nbsp;Bug&nbsp;» et «&nbsp;Performance&nbsp;» sous «&nbsp;Ça ne marchait
                  pas&nbsp;»). «&nbsp;Plus besoin&nbsp;» reste à part&nbsp;: il est trop ambigu
                  pour être rattaché.
                </Typography>
              )}
              {this.renderReasonBars(motifsAffiches, total, 'reason_code', REASON_LABELS)}
            </Paper>

            {byFamily.length > 0 && (
              <Paper variant="outlined" sx={{ p: 2 }}>
                <Typography variant="subtitle1" sx={{ mb: 2 }}>
                  Par grande catégorie{' '}
                  <Typography component="span" variant="caption" color="text.secondary">
                    (chaque motif appartient à une seule catégorie · total {total})
                  </Typography>
                </Typography>
                {this.renderReasonBars(byFamily, total, 'family', FAMILY_LABELS)}
              </Paper>
            )}

            <Stack direction={{ xs: 'column', md: 'row' }} spacing={3}>
              <Paper variant="outlined" sx={{ p: 2, flex: 1 }}>
                <Typography variant="subtitle1" sx={{ mb: 1 }}>Par version de plugin</Typography>
                {this.renderBreakdownTable(byVersion, 'plugin_version', 'Version', '(non précisée)')}
              </Paper>
              <Paper variant="outlined" sx={{ p: 2, flex: 1 }}>
                <Typography variant="subtitle1" sx={{ mb: 1 }}>Par langue</Typography>
                {this.renderBreakdownTable(byLocale, 'locale', 'Langue', '(non précisée)')}
              </Paper>
            </Stack>

            {this.renderVerbatims()}
          </Stack>
        )}
      </Box>
    );
  }
}

export default DeactivationFeedback;
